import type { MedplumClient } from '@medplum/core';
import { fetchBot } from '../services/services';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64Safe);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Ensure a push subscription exists and is saved on the server for the given practitioner.
 *
 * Behavior:
 * - If Push/ServiceWorker is unsupported, no-ops.
 * - If Notification permission is not 'granted', no-ops (user must opt-in via prompt UI).
 * - If a service worker is not registered yet, registers '/push-notification-service-worker.js'.
 * - If an existing subscription is found, re-sends it to the bot for registration.
 * - If none exists and VAPID key is configured, creates a new subscription and registers it.
 */
export async function ensurePushSubscription(medplum: MedplumClient, practitionerId: string): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    const supportsSW = 'serviceWorker' in navigator;
    const supportsPush = 'PushManager' in window;
    const supportsNotif = 'Notification' in window;

    if (!supportsSW || !supportsPush || !supportsNotif) {
      return;
    }

    if (Notification.permission !== 'granted') {
      // Respect user choice; prompt elsewhere (PushNotificationsPrompt)
      return;
    }

    // Ensure SW is registered and ready
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register('/push-notification-service-worker.js');
    }
    await navigator.serviceWorker.ready;

    // Retrieve existing subscription or create a new one
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
      if (!vapidPublicKey) {
        // eslint-disable-next-line no-console
        console.warn('VAPID public key not configured. Cannot create push subscription.');
        return;
      }
      subscription = await registration.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
        userVisibleOnly: true,
      });
    }

    // Send subscription to Medplum via bot, attached to Practitioner
    try {
      await fetchBot(medplum, 'save-push-subscription', {
        practitionerId,
        subscription,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to save push subscription via bot', err);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('ensurePushSubscription failed', err);
  }
}

/**
 * Closes the "Incoming Call" push for a call once it stops ringing in this tab (BAR-2531).
 *
 * The bots tag that push `call-<sid>` with the call's parent sid when it has one and its own
 * sid otherwise — what `resolveStableCallLegSid` returns. Nothing on the server can take it
 * down for a call that was cancelled rather than missed, and a lingering one is worse than
 * clutter: its Accept answers whatever call is ringing in the tab when it is clicked.
 *
 * Best-effort and never throws; it runs inside Twilio call event handlers.
 */
export async function closeIncomingCallNotifications(callSid: string | undefined): Promise<void> {
  if (!callSid || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const notifications = (await registration?.getNotifications({ tag: `call-${callSid}` })) ?? [];
    notifications.forEach((notification) => notification.close());
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to close the incoming call notification', err);
  }
}
